import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Lingua Craft';
const SITE_URL = 'https://linguacraft.de';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;

export interface SEOProps {
  title: string;
  description: string;
  /** Canonical path, e.g. "/questions" – site URL is prepended automatically */
  canonicalPath?: string;
  ogImage?: string;
  /** Structured data (JSON-LD) – pass a plain object, it will be serialised */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Noindex pages (private/admin) */
  noindex?: boolean;
}

/**
 * Returns a <Helmet> element with full SEO meta tags.
 * Usage: drop <SEO ... /> inside a page component's JSX.
 */
export const SEO = ({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
  noindex = false,
}: SEOProps) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath}`
    : undefined;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
